import { useSnackbar } from "notistack";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import { SketchPicker } from "react-color";

import { useTranslation } from "react-i18next";
import RemoveIcon from "../../../components/RemoveIcon";
import ControlledFileInput from "../../../components/controlled/ControlledFileInput";
import ControlledTextField from "../../../components/controlled/ControlledTextField";
import {
	useGetCompanyBrandingQuery,
	useRemoveImageFromCompanyBrandingMutation,
	useUpdateCompanyBrandingMutation,
} from "../brandingApiSlice";

const BrandingForm = ({ companyId }) => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const {
		data: branding,
		error,
		isLoading,
	} = useGetCompanyBrandingQuery(companyId);
	const [updateBranding, { isLoading: isUpdating }] =
		useUpdateCompanyBrandingMutation();
	const [removeImage, { isLoading: isRemoving }] =
		useRemoveImageFromCompanyBrandingMutation();

	const {
		handleSubmit,
		control,
		formState: { errors },
		setValue,
		setError,
	} = useForm({
		// defaultValues: { loginBgColor: "#ffffff", loginBoxColor: "#000000" },
		// resolver: yupResolver(formSchema),
		shouldFocusError: true,
	});

	useEffect(() => {
		setTimeout(() => {
			setValue("domain", branding?.domain || "");
			setValue("customerDomain", branding?.customerDomain || "");
			setValue("loginBgColor", { rgb: branding?.loginBgColor || "" });
			setValue("loginBoxColor", { rgb: branding?.loginBoxColor || "" });
			setValue("barColor", { rgb: branding?.barColor || "" });
			setValue("buttonColor", { rgb: branding?.buttonColor || "" });
			setValue("mailingTitle", branding?.mailingTitle || "");
		}, 200);
	}, [isLoading]);

	const onSubmit = async (data) => {
		const formData = new FormData();
		formData.append("logoFile", data.logo);
		formData.append("logoVerticalFile", data.logoVertical);
		formData.append("backgroundImageFile", data.backgroundImage);
		formData.append("mailingLogoFile", data.mailingLogo);

		for (const item in data) {
			if (item.includes("Color")) {
				const colorToAppend = data[item]?.rgb
					? data[item].rgb.r > -1
						? `rgba(${data[item].rgb.r}, ${data[item].rgb.g}, ${data[item].rgb.b}, ${data[item].rgb.a})`
						: data[item].rgb
					: undefined;

				if (colorToAppend) {
					formData.append(item, colorToAppend);
				}
			} else {
				formData.append(item, data[item]);
			}
		}

		await updateBranding({ companyId, formData })
			.unwrap()
			.then((_payload) => {
				enqueueSnackbar(t("updated_clients_branding"), {
					variant: "success",
				});
			})
			.catch((error) => {
				if (error?.data?.errors) {
					const errors = error.data.errors;
					Object.entries(errors).forEach(([key, message]) => {
						setError(key);
						enqueueSnackbar(message[0], {
							variant: "error",
							preventDuplicate: true,
						});
					});
				} else {
					enqueueSnackbar(error?.data?.message || t("default_error"), {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
	};

	const handleRemoveFile = async (name) => {
		const formData = new FormData();
		formData.append(name, null);

		await removeImage({ companyId, formData })
			.unwrap()
			.then((_payload) => {
				enqueueSnackbar(t("removed_graphic"), {
					variant: "success",
				});
			})
			.catch((error) => {
				if (error?.data?.errors) {
					const errors = error.data.errors;
					Object.entries(errors).forEach(([key, message]) => {
						setError(key);
						enqueueSnackbar(message[0], {
							variant: "error",
							preventDuplicate: true,
						});
					});
				} else {
					enqueueSnackbar(error?.data?.message || t("default_error"), {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
	};

	if (isLoading) {
		return <h1>Loading...</h1>;
	}

	return (
		<Box sx={{ display: "flex" }}>
			<Box
				component="form"
				noValidate
				onSubmit={handleSubmit(onSubmit)}
				sx={{ p: 0 }}
			>
				<Grid container spacing={2} rowSpacing={4}>
					<Grid size={12}>
						<Box sx={{ display: "flex", width: "600px" }}>
							<ControlledTextField
								control={control}
								name="domain"
								label={t("domain")}
								error={!!errors?.domain}
								labelXs={6}
								fieldXs={6}
							/>
						</Box>
					</Grid>
					<Grid size={12}>
						<Box sx={{ display: "flex", width: "600px" }}>
							<ControlledTextField
								control={control}
								name="customerDomain"
								label={t("client_domain")}
								error={!!errors?.customerDomain}
								labelXs={6}
								fieldXs={6}
							/>
						</Box>
					</Grid>
					<Grid size={12}>
						<Box sx={{ display: "flex", gap: 6 }}>
							<Box>
								<Typography
									sx={{ fontWeight: 600, mb: 1, display: "inline-block" }}
								>
									{t("login_color")}:{" "}
								</Typography>
								<Controller
									control={control}
									name={"loginBoxColor"}
									defaultValue={""}
									render={({ field: { onChange, value } }) => {
										return (
											<SketchPicker
												onChangeComplete={onChange}
												color={value.rgb}
											/>
										);
									}}
								></Controller>
							</Box>
							<Box>
								<Typography
									sx={{ fontWeight: 600, mb: 1, display: "inline-block" }}
								>
									{t("login_background_color")}:{" "}
								</Typography>
								<Controller
									control={control}
									name={"loginBgColor"}
									defaultValue={""}
									render={({ field: { onChange, value } }) => {
										return (
											<SketchPicker
												onChangeComplete={onChange}
												color={value.rgb}
											/>
										);
									}}
								></Controller>
							</Box>
							<Box>
								<Typography
									sx={{ fontWeight: 600, mb: 1, display: "inline-block" }}
								>
									{t("bar_color")}:{" "}
								</Typography>
								<Controller
									control={control}
									name={"barColor"}
									defaultValue={""}
									render={({ field: { onChange, value } }) => {
										return (
											<SketchPicker
												onChangeComplete={onChange}
												color={value.rgb}
											/>
										);
									}}
								></Controller>
							</Box>
							<Box>
								<Typography
									sx={{ fontWeight: 600, mb: 1, display: "inline-block" }}
								>
									{t("button_color")}:{" "}
								</Typography>
								<Controller
									control={control}
									name={"buttonColor"}
									defaultValue={""}
									render={({ field: { onChange, value } }) => {
										return (
											<SketchPicker
												onChangeComplete={onChange}
												color={value.rgb}
											/>
										);
									}}
								></Controller>
							</Box>
						</Box>
					</Grid>
					<Grid size={12}>
						<Box sx={{ display: "flex", alignItems: "center", width: "600px" }}>
							<ControlledFileInput
								control={control}
								name="logo"
								type="file"
								label={t("logo")}
								error={!!errors?.logoBig}
								labelXs={6}
								fieldXs={6}
							/>
							{branding?.logo && (
								<>
									<ImagePreview url={branding?.logo} />
									<RemoveIcon onClick={() => handleRemoveFile("logo")} />
								</>
							)}
						</Box>
					</Grid>
					<Grid size={12}>
						<Box sx={{ display: "flex", alignItems: "center", width: "600px" }}>
							<ControlledFileInput
								control={control}
								name="logoVertical"
								type="file"
								label={t("logo_vertical")}
								error={!!errors?.logoVertical}
								labelXs={6}
								fieldXs={6}
							/>
							{branding?.logoVertical && (
								<>
									<ImagePreview url={branding?.logoVertical} />
									<RemoveIcon
										onClick={() => handleRemoveFile("logoVertical")}
									/>
								</>
							)}
						</Box>
					</Grid>
					<Grid size={12}>
						<Box sx={{ display: "flex", alignItems: "center", width: "600px" }}>
							<ControlledFileInput
								control={control}
								name="backgroundImage"
								type="file"
								label={t("login_background_image")}
								error={!!errors?.backgroundImage}
								labelXs={6}
								fieldXs={6}
							/>
							{branding?.backgroundImage && (
								<>
									<ImagePreview url={branding?.backgroundImage} />
									<RemoveIcon
										onClick={() => handleRemoveFile("backgroundImage")}
									/>
								</>
							)}
						</Box>
					</Grid>
					<Grid size={12}>
						<Box sx={{ display: "flex", alignItems: "center", width: "600px" }}>
							<ControlledFileInput
								control={control}
								name="mailingLogo"
								type="file"
								label={t("mailing_logo")}
								error={!!errors?.mailingLogo}
								labelXs={6}
								fieldXs={6}
							/>
							{branding?.mailingLogo && (
								<>
									<ImagePreview url={branding?.mailingLogo} />
									<RemoveIcon onClick={() => handleRemoveFile("mailingLogo")} />
								</>
							)}
						</Box>
					</Grid>
					<Grid size={12}>
						<Box sx={{ display: "flex", width: "600px" }}>
							<ControlledTextField
								control={control}
								name="mailingTitle"
								label={t("mailing_title")}
								error={!!errors?.mailingTitle}
								labelXs={6}
								fieldXs={6}
							/>
						</Box>
					</Grid>
					<Grid size={12}>
						<Button loading={isUpdating} type="submit" variant="contained">
							{t("save")}
						</Button>
					</Grid>
				</Grid>
			</Box>
		</Box>
	);
};

export default BrandingForm;

const ImagePreview = ({ url }) => {
	const { t } = useTranslation();
	const [open, setOpen] = React.useState(false);
	const handleOpen = () => setOpen(true);
	const handleClose = () => setOpen(false);

	return (
		<>
			<Button onClick={handleOpen}>{t("preview")}</Button>
			<Modal open={open} onClose={handleClose}>
				<Box
					sx={{ width: "100%", height: "100%", padding: "20px 60px 60px 60px" }}
				>
					<Box sx={{ display: "flex", justifyContent: "end" }}>
						<Button sx={{ color: "#fff" }} onClick={handleClose}>
							{t("close")}
						</Button>
					</Box>
					<img
						style={{ width: "100%", height: "100%", objectFit: "contain" }}
						src={url}
						alt=""
					/>
				</Box>
			</Modal>
		</>
	);
};
